import React, { Fragment, useMemo, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector, useDispatch } from 'react-redux';
import { isSameDay, format } from 'date-fns';
import { RANGES } from 'components/DateRangePicker/ranges';
import { Link } from 'react-router-dom';
import _isEqual from 'lodash/isEqual';

import TableTopFilters from 'components/TableTopFilters';
import LoaderBar from 'components/LoaderBar';
import DashboardContainer from 'components/DashboardContainer';
import SearchWithFilters from 'components/SearchWithFilters';
import Table from 'components/Table';
import StatusBadge from 'components/StatusBadge';

import { MONTH_DAY_HOUR, DATE_FOR_API } from 'utils/dateFormat';
import { SEARCH_FILTERS, STATUS_FILTERS } from './helpers';
import DisputeActions from './DisputeActions';
import schema, { FIELD_NAMES, defaultValues as _defaultValues } from './schema';
import { disputesTable as TEXT } from 'locales/en/dispute';
import {
  disputeAllSelector,
  getDisputes,
  setParams,
  setSearchTerm,
  setSearchFilter,
  resetSearch,
  searchDisputes,
  exportDisputes,
} from 'redux/slices/dispute';
import { usePrevious } from 'hooks';

const Disputes = () => {
  const [activeItem, setActiveItem] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getDisputes());

    return () => {
      dispatch(resetSearch());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, params, pagination, isLoading, searchTerm, searchFilter } =
    useSelector((state) => disputeAllSelector(state));

  const toggleActiveItem = (id) => {
    if (activeItem === id) {
      setActiveItem(null);
    } else {
      setActiveItem(id);
    }
  };
  const company = useSelector((state) => state.company.current);
  const prevCompany = usePrevious(company);
  const prevParams = usePrevious(params);
  const prevSearchFilter = usePrevious(searchFilter);

  useEffect(() => {
    if (prevCompany !== null && prevCompany !== company) {
      dispatch(
        setParams({
          page: 1,
        })
      );
      dispatch(getDisputes());
    }
  }, [company, dispatch, prevCompany]);

  useEffect(() => {
    if (
      (prevParams !== null && !_isEqual(params, prevParams)) ||
      (!!prevSearchFilter && searchFilter === undefined)
    ) {
      dispatch(getDisputes());
    }
  }, [dispatch, params, prevParams, prevSearchFilter, searchFilter]);

  const onApplyFilter = (data) => {
    const { status, amountCheckbox, ...rest } = data;
    let statusSet = new Set();

    for (const property in status) {
      if (status[property]) {
        statusSet.add(property);
      }
    }

    const statusFilter = Array.from(statusSet).join(',');
    let params = {
      status: statusFilter,
      ...rest,
    };

    dispatch(setParams({ ...params, page: 1 }));
  };

  const onApplyDate = (date) => {
    dispatch(
      setParams({
        from: format(date.startDate, DATE_FOR_API),
        to: format(date.endDate, DATE_FOR_API),
        page: 1,
      })
    );
  };

  const onChangePerPage = (per_page) => {
    dispatch(
      setParams({
        per_page,
        page: 1,
      })
    );
  };

  const onChangePage = ({ selected }) => {
    dispatch(
      setParams({
        page: selected + 1,
      })
    );
  };

  const range = useMemo(() => {
    let id =
      RANGES.find(
        (item) =>
          isSameDay(new Date(item.range().startDate), new Date(params.from)) &&
          isSameDay(new Date(item.range().endDate), new Date(params.to))
      )?.range()?.id || RANGES[RANGES.length - 1]?.range()?.id;

    return {
      startDate: new Date(params.from),
      endDate: new Date(params.to),
      id,
      key: 'selection',
    };
  }, [params.from, params.to]);

  const onChangeSearchTerm = (event) => {
    dispatch(setSearchTerm(event.target.value || ''));
  };

  const onSelectSearchFilter = (item, { action }) => {
    if (action === 'clear') {
      dispatch(resetSearch());
    } else {
      dispatch(setSearchFilter(item));
    }
  };

  const onSearch = (e) => {
    dispatch(searchDisputes());
  };

  const onExport = () => dispatch(exportDisputes());
  return (
    <DashboardContainer header={TEXT.header}>
      <LoaderBar isVisible={isLoading} />
      <SearchWithFilters
        onChangeInput={onChangeSearchTerm}
        onChangeFilter={onSelectSearchFilter}
        onSearch={onSearch}
        searchTerm={searchTerm}
        options={SEARCH_FILTERS}
        placeholder={TEXT.search}
        searchFilter={searchFilter}
        hasFilters
        isClearable
      />
      <Table
        topContent={
          <TableTopFilters
            exportHeader={TEXT.export}
            onApplyFilter={onApplyFilter}
            statusFilter={STATUS_FILTERS}
            defaultValues={_defaultValues(params)}
            schema={schema}
            fieldNames={FIELD_NAMES}
            onApplyDate={onApplyDate}
            range={range}
            onConfirmExport={onExport}
            isExportDisabled={!data.length}
            singleSelection={{ reason: true }}
          />
        }
        hasData={!!data.length}
        titles={[
          TEXT.id,
          { name: TEXT.amount, class: 'text-center' },
          { name: TEXT.fee, class: 'text-center' },
          { name: TEXT.transactionId, class: 'text-center' },
          { name: TEXT.reason, class: 'text-center' },
          { name: TEXT.status, class: 'text-center' },
          { name: TEXT.date, class: 'text-center' },
          { name: TEXT.daysToDefend, class: 'text-center' },
          { name: TEXT.defenceSubmitted, class: 'text-center' },
          '',
        ]}
        pagination={pagination}
        gridClassName="grid-disputes"
        onClickPerPage={onChangePerPage}
        onPageChange={onChangePage}
      >
        {!!data.length &&
          data.map((item, idx) => {
            const dataIndexNumber = data.length - 1 === idx ? 'last' : idx;

            return (
              <Fragment key={item.id}>
                <Link
                  data-index-number={dataIndexNumber}
                  className="underline text-javelin-gray-dark text-xs py-3 px-5"
                  to={`/disputes/${item.id}`}
                >
                  {item.id}
                </Link>
                <span
                  data-index-number={dataIndexNumber}
                  className="text-javelin-gray-dark text-xs py-3 px-5 font-bold text-center"
                >
                  {item.amountToDisplay}
                </span>
                <span
                  data-index-number={dataIndexNumber}
                  className="text-javelin-gray-dark text-xs py-3 px-5 font-bold text-center"
                >
                  {item.feeToDisplay}
                </span>
                <Link
                  data-index-number={dataIndexNumber}
                  className="underline text-javelin-gray-dark text-xs py-3 px-5"
                  to={`/transactions/${item.transaction_id}`}
                >
                  {item.transaction_id}
                </Link>
                <span
                  data-index-number={dataIndexNumber}
                  className="text-javelin-gray-dark text-xs py-3 px-5 text-center"
                >
                  {item.reason}
                </span>
                <span
                  data-index-number={dataIndexNumber}
                  className="text-javelin-gray-dark text-xs py-3 pl-5 text-center"
                >
                  <StatusBadge status={item.status} className="ml-0" />
                </span>
                <span
                  data-index-number={dataIndexNumber}
                  className="text-javelin-gray-dark text-xs py-3 px-5 text-center"
                >
                  {format(new Date(item.created_at), MONTH_DAY_HOUR) || '-'}
                </span>
                <span
                  data-index-number={dataIndexNumber}
                  className="text-javelin-gray-dark text-xs py-3 px-5 text-center"
                >
                  {item.days_to_defend}
                </span>
                <span
                  data-index-number={dataIndexNumber}
                  className="text-javelin-gray-dark text-xs py-3 px-5 text-center"
                >
                  {item.defense_submitted ? (
                    <FontAwesomeIcon
                      icon="check"
                      size="lg"
                      className="text-green-lowRisk my-auto p-1 bg-green-lowRisk bg-opacity-20 rounded-full"
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon="times"
                      size="lg"
                      className="fa-w-16 p-0.5 text-javelin-danger bg-javelin-danger bg-opacity-20 text-opacity-50 rounded-full"
                    />
                  )}
                </span>
                <span
                  data-index-number={dataIndexNumber}
                  className="text-javelin-gray-dark text-xs relative py-3 text-right pr-5"
                >
                  <DisputeActions
                    onClickOpen={() => toggleActiveItem(item.id)}
                    isOpen={activeItem === item.id}
                    onClose={() => setActiveItem(null)}
                    dispute={item}
                    position={idx <= data.length - 4 ? 'bottom' : 'top'}
                  />
                </span>
              </Fragment>
            );
          })}
      </Table>
    </DashboardContainer>
  );
};

export default Disputes;
